import { IconButton, Tooltip } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { fetchDepots, fetchThings } from '../../action/depotsCommands';
import { fetchLocations } from '../../action/locationsCommands';
import { Location, Thing } from '../../api/types';
import PageHeader from '../../components/PageHeader';
import PageHeadline from '../../components/PageHeadline';
import PageSubtitle from '../../components/PageSubtitle';
import SearchBar from '../../components/SearchBar';
import ReloadIcon from '../../components/icons/ReloadIcon';
import { makeDepotDetailsUrl } from '../../routes';
import { makePermittedDepotListSelector, thingListSelector } from '../../selector/depotsSelector';
import { makePermittedLocationListSelector } from '../../selector/locationsSelector';
import { tenantSelector } from '../../selector/authenticationSelector';
import DepotCard, { DepotWithAddress } from './DepotCard';
import ObjectLimitBanner from '../../components/ObjectLimitBanner';

const DepotsPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const locations = useSelector(makePermittedLocationListSelector());
    const permittedDepots = useSelector(makePermittedDepotListSelector());
    const things = useSelector(thingListSelector);
    const selectedTenant = useSelector(tenantSelector);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const { t } = useTranslation();

    useEffect(() => {
        dispatch(fetchDepots({}));
        dispatch(fetchLocations({}));
        dispatch(fetchThings({}));
    }, []);

    const navigateToDepot = (depotId: string) => {
        navigate(makeDepotDetailsUrl(depotId));
    };

    const reloadDepots = () => {
        dispatch(fetchDepots({}));
        dispatch(fetchLocations({}));
    };

    const locationMap = useMemo(() => {
        const map = new Map<string, Location>();
        locations.forEach((location) => map.set(location.id, location));
        return map;
    }, [locations]);

    const thingMap = useMemo(() => {
        const map = new Map<string, Thing>();
        things.forEach((thing) => map.set(thing.id, thing));
        return map;
    }, [things]);

    const statusLabels = useMemo(() => ({
        enabled: t('depots.status.enabled').toLowerCase(),
        disabled: t('depots.status.disabled').toLowerCase(),
        bookable: t('depots.status.bookable').toLowerCase(),
        notBookable: t('depots.status.not_bookable').toLowerCase(),
        online: t('depots.status.online').toLowerCase(),
        offline: t('depots.status.offline').toLowerCase(),
    }), [t]);

    const filteredDepots = permittedDepots.filter((depot) => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();

        if (depot.name.toLowerCase().includes(term)) return true;

        const location = locationMap.get(depot.locationId);
        if (location?.address) {
            const addr = location.address;
            if (
                addr.street?.toLowerCase().includes(term) ||
                addr.postalCode?.toLowerCase().includes(term) ||
                addr.city?.toLowerCase().includes(term)
            ) return true;
        }

        const isEnabled = selectedTenant === 'segmenta' ? depot.enabled : depot.status?.enabled;
        const enabledLabel = isEnabled ? statusLabels.enabled : statusLabels.disabled;
        if (enabledLabel === term || enabledLabel.startsWith(term)) return true;

        const bookableLabel = depot.status?.bookable ? statusLabels.bookable : statusLabels.notBookable;
        if (bookableLabel === term || bookableLabel.startsWith(term)) return true;

        const thing = thingMap.get(depot.thingsId);
        const onlineStatus = thing?.minutesFromLastSeen !== undefined
            ? (thing.minutesFromLastSeen <= 10 ? 'online' : 'offline')
            : undefined;
        if (onlineStatus) {
            const onlineLabel = onlineStatus === 'online' ? statusLabels.online : statusLabels.offline;
            if (onlineLabel === term || onlineLabel.startsWith(term)) return true;
        }

        return false;
    });

    const depotList = filteredDepots.sort((depot1, depot2) => {
        if (depot1.name < depot2.name) return -1;
        if (depot1.name > depot2.name) return 1;
        return 0;
    });

    const depotsWithAddress = depotList
        .filter((depot) => !depot.isFictional)
        .map((depot) => {
            const location = locationMap.get(depot.locationId);
            if (location) {
                return {
                    ...depot,
                    address: location.address,
                    status: location.status,
                };
            } else {
                return depot;
            }
        }) as DepotWithAddress[];

    return (
        <div>
            <PageHeader>
                <div className={'flex-grow'}>
                    <PageHeadline>
                        {/* Same string as the sidebar entry, not a copy of it:
                            the page and the menu item must never drift apart
                            the way "Stationsübersicht" under "Zugangsverwaltung"
                            did. */}
                        {t('side_bar.access_management')}
                    </PageHeadline>
                    <PageSubtitle>
                        {depotsWithAddress.length}{' '}
                        {depotsWithAddress.length === 1
                            ? t('common.cabinets')
                            : t('common.cabinets_other')}
                    </PageSubtitle>
                </div>
                <SearchBar
                    className={'hidden sm:block'}
                    placeholder={t('depots.search_placeholder')}
                    value={searchTerm}
                    onChange={setSearchTerm}
                />
                <Tooltip title={'Reload'}>
                    <IconButton
                        className={'text-foreground -mr-2'}
                        onClick={reloadDepots}
                    >
                        <ReloadIcon />
                    </IconButton>
                </Tooltip>
            </PageHeader>
            <div className={'mb-4'}>
                <ObjectLimitBanner />
            </div>
            <SearchBar
                className={'w-full sm:hidden mb-5'}
                placeholder={t('depots.search_placeholder')}
                value={searchTerm}
                onChange={setSearchTerm}
            />
            <section
                className={'grid sm:grid-cols-2 max-w-full gap-y-2 gap-x-5'}
            >
                {depotsWithAddress.map((depot) => (
                    <DepotCard
                        key={depot.id}
                        onPress={() => navigateToDepot(depot.id)}
                        depot={depot}
                        thing={thingMap.get(depot.thingsId)}
                    />
                ))}
            </section>
        </div>
    );
};

export default DepotsPage;
